import type { FC } from "react";
import { useContext } from "react";

import styled from "styled-components";

import type { IWidgetModelTypesType } from "app/components/widget/widget-types/Widget";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, percentageSignFilter, timeFilter } from "app/filters/ngFilters";
import { IndustryOverviewContext } from "app/pages/industry-analysis/overview/components/IndustryOverviewContext/IndustryOverviewContext";
import { WithIndustryOverviewContextHOC } from "app/pages/industry-analysis/overview/components/IndustryOverviewContext/withIndustryOverviewContextHOC";
import { NoData } from "app/pages/keyword-analysis/TotalTrafficPage/Components/UtilityComponents";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";

import type { RawData } from "../../types";
import Header from "../common/Header";
import { initAverageData } from "../utils";

import Cube from "./components/Cube";
import TotalVisits from "./components/TotalVisits";

const KeyIndicatorsWrapper = styled.div`
    width: auto;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const TotalVisitsContainer = styled.div`
    width: 50%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const CubeContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 50%;
`;

const ContentContainer = styled.div`
    display: flex;
    justify-content: space-between;
    flex-grow: 1;
    padding: 8px;

    @media (max-width: 1100px) {
        ${TotalVisitsContainer} {
            width: 44%;
        }
        ${CubeContainer} {
            width: 56%;
        }
    }
`;

const CubeRow = styled.div`
    display: flex;
    height: 100%;
    gap: 8px;
`;

const NoDataContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    align-content: center;
    height: 300px;
`;

interface IKeyIndicators {
    data?: RawData;
    isLoading: boolean;
}

const KeyIndicators: FC<IKeyIndicators> = ({ data, isLoading }) => {
    const totalVisitsData = data?.TotalVisits;
    const averageData = data?.Average;
    const isDecrease = totalVisitsData?.Change && totalVisitsData?.Change < 0;
    const { services } = useContext(IndustryOverviewContext);

    const getFilteredValue = (num: number | null | undefined, isPercentage = false) => {
        // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
        return isPercentage ? percentageSignFilter()(num, 2) : abbrNumberFilter()(num, 0);
    };

    const { PagesPerVisit, TotalUnbouncedVisits, VisitsPerVisitor, VisitDuration } = initAverageData(averageData);

    const selectedCustomIndustryHasSegments = CategoryServiceUtils.selectedCategoryHasSegments();

    return (
        <KeyIndicatorsWrapper data-automation="KeyIndicators">
            <Header
                title={translate("industryoverview.key.indicators.header.title")}
                tooltip={translate("industryoverview.key.indicators.header.title.tooltip")}
                isLoading={isLoading}
                services={services}
                addToDashboardProps={{
                    isVisible: false, // when we want to enable it just switch to true
                    metric: "EngagementVisits",
                    type: "SingleMetric" as IWidgetModelTypesType,
                }}
            />
            {!data && !isLoading ? (
                <NoDataContainer>
                    <NoData
                        noDataTitleKey="global.nodata.notavilable"
                        noDataSubTitleKey="workspaces.marketing.nodata.subtitle"
                    />
                </NoDataContainer>
            ) : (
                <ContentContainer>
                    <TotalVisitsContainer>
                        <TotalVisits
                            absolute={getFilteredValue(totalVisitsData?.Value)}
                            change={getFilteredValue(totalVisitsData?.Change, true)}
                            isDecrease={isDecrease}
                            isLoading={isLoading}
                        />
                    </TotalVisitsContainer>
                    <CubeContainer>
                        <CubeRow>
                            {averageData?.Visits && !selectedCustomIndustryHasSegments ? (
                                <Cube
                                    title={translate("industryoverview.key.indicators.cube.visits.per.visitor")}
                                    iconName="month"
                                    valNum={getFilteredValue(VisitsPerVisitor)}
                                    tooltip={translate(
                                        "industryoverview.key.indicators.cube.visits.per.visitor.tooltip",
                                    )}
                                    isLoading={isLoading}
                                />
                            ) : null}
                            <Cube
                                title={translate("industryoverview.key.indicators.cube.total.unbounced.visits")}
                                iconName="bounce-rate-2"
                                valNum={getFilteredValue(TotalUnbouncedVisits)}
                                tooltip={translate(
                                    "industryoverview.key.indicators.cube.total.unbounced.visits.tooltip",
                                )}
                                isLoading={isLoading}
                            />
                        </CubeRow>
                        <CubeRow>
                            <Cube
                                title={translate("industryoverview.key.indicators.cube.pages.per.visit")}
                                iconName="pages-per-visit"
                                valNum={getFilteredValue(PagesPerVisit)}
                                tooltip={translate("industryoverview.key.indicators.cube.pages.per.visit.tooltip")}
                                isLoading={isLoading}
                            />
                            <Cube
                                title={translate("industryoverview.key.indicators.cube.visit.duration")}
                                iconName="avg-visit-duration"
                                valNum={timeFilter()(VisitDuration, null)}
                                tooltip={translate("industryoverview.key.indicators.cube.visit.duration.tooltip")}
                                isLoading={isLoading}
                            />
                        </CubeRow>
                    </CubeContainer>
                </ContentContainer>
            )}
        </KeyIndicatorsWrapper>
    );
};

export default WithIndustryOverviewContextHOC(KeyIndicators);
